/**
 * Константы модуля «Нарушения» (Part 1).
 *
 * Здесь лежат только подписи операторов, статусов и карта типов контролов.
 * Списка метрик здесь нет и быть не должно: единственный источник метрик —
 * GET /admin/violation-rule-catalog (ТЗ, раздел «ЗАПРЕТ НА ХАРДКОД»).
 */

export const VIOLATION_STATUS = {
  ACTIVE: 'active',
  RESOLVED: 'resolved',
};

const STATUS_BADGES = {
  active: { code: 'ACTIVE', label: 'Активно', modifier: 'active' },
  resolved: { code: 'RESOLVED', label: 'Завершено', modifier: 'resolved' },
};

export const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'active', label: 'Активные' },
  { value: 'resolved', label: 'Завершённые' },
];

/**
 * FE-FIX15. Параметр запроса — analytics_excluded; «Все» означает его
 * отсутствие, а не `analytics_excluded=` с пустым значением.
 */
export const ANALYTICS_FILTER_OPTIONS = [
  { value: '', label: 'Все события' },
  { value: 'false', label: 'Учитываемые' },
  { value: 'true', label: 'Исключённые' },
  { value: 'possible_duplicate', label: 'Возможные дубли' },
  { value: 'confirmed_duplicate', label: 'Подтверждённые дубли' },
];

// Значения фильтра «Аналитика», которые уходят в backend как duplicate_state.
export const DUPLICATE_FILTER_VALUES = ['possible_duplicate', 'confirmed_duplicate'];

export const ENABLED_FILTER_OPTIONS = [
  { value: '', label: 'Все состояния' },
  { value: 'true', label: 'Включено' },
  { value: 'false', label: 'Отключено' },
];

export const OPERATORS = ['gt', 'gte', 'lt', 'lte', 'eq', 'ne'];

export const OPERATOR_LABELS = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  ne: '≠',
};

const VALUE_TYPE_CONTROL = {
  integer: 'integer',
  number: 'number',
  boolean: 'boolean',
  enum: 'enum',
  string: 'string',
};

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const DEFAULT_PAGE_SIZE = 50;

export const getOperatorLabel = (operator) => {
  if (!operator) return '';
  return OPERATOR_LABELS[operator] ?? operator;
};

export const getStatusBadge = (status) => {
  const known = STATUS_BADGES[status];
  if (known) return known;
  const raw = status == null || status === '' ? '—' : String(status);
  return { code: raw.toUpperCase(), label: raw, modifier: 'unknown' };
};

/**
 * Неизвестный value_type деградирует до текстового поля, а не ломает форму:
 * backend может добавить новый тип метрики без правок фронта.
 */
export const getValueTypeControl = (valueType) => VALUE_TYPE_CONTROL[valueType] ?? 'string';

/* ------------------------------------------------------------------ Part 2 */

export const WEEKDAYS = [
  { value: 0, label: 'Понедельник' },
  { value: 1, label: 'Вторник' },
  { value: 2, label: 'Среда' },
  { value: 3, label: 'Четверг' },
  { value: 4, label: 'Пятница' },
  { value: 5, label: 'Суббота' },
  { value: 6, label: 'Воскресенье' },
];

export const getWeekdayLabel = (weekday) =>
  WEEKDAYS.find((day) => day.value === Number(weekday))?.label ?? '—';

export const REVIEW_VERDICT = {
  UNREVIEWED: 'unreviewed',
  CONFIRMED: 'confirmed',
  FALSE_POSITIVE: 'false_positive',
  UNCERTAIN: 'uncertain',
};

/** Три действия оператора по макету ТЗ 7.3; unreviewed — состояние, а не кнопка. */
export const REVIEW_ACTIONS = [
  { verdict: REVIEW_VERDICT.CONFIRMED, label: 'Подтвердить' },
  { verdict: REVIEW_VERDICT.FALSE_POSITIVE, label: 'Ошибка' },
  { verdict: REVIEW_VERDICT.UNCERTAIN, label: 'Не уверен' },
];

const VERDICT_LABELS = {
  unreviewed: 'Не проверено',
  confirmed: 'Подтверждено человеком',
  false_positive: 'Ошибка системы',
  uncertain: 'Недостаточно данных',
};

export const getVerdictLabel = (verdict) => {
  if (!verdict) return '—';
  return VERDICT_LABELS[verdict] ?? verdict;
};

export const EVIDENCE_STATUS = {
  PROCESSING: 'processing',
  READY: 'ready',
  UNAVAILABLE: 'unavailable',
  /**
   * Локальный статус фронта: список Evidence пуст. Backend такого не отдаёт,
   * но «фото ещё не снято» и «фото недоступно» — разные сообщения
   * (Evidence Addendum v1.0, разделы 4 и 6).
   */
  EMPTY: 'empty',
};

/**
 * ТЗ 5.2: поддержать минимум эти четыре кода и безопасно показывать
 * неизвестные будущие значения нейтральным текстом.
 */
const RESOLUTION_REASON_LABELS = {
  condition_cleared: 'Условие перестало выполняться',
  schedule_window_ended: 'Окончание окна расписания',
  schedule_changed: 'Расписание изменено',
  rule_disabled: 'Правило отключено',
};

export const getResolutionReasonLabel = (code) => {
  if (!code) return '—';
  // Task 5 / ТЗ 8-11: неизвестный код завершения — нейтральный текст,
  // а не сырой код бэкенда, показанный оператору как есть.
  return RESOLUTION_REASON_LABELS[code] ?? 'Причина не указана';
};

export const DEFAULT_TIMEZONE = 'Europe/Moscow';

/** Если Intl.supportedValuesOf недоступен, хватает набора российских зон. */
const FALLBACK_TIMEZONES = [
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Samara',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
];

export const listTimezones = () => {
  try {
    const zones = Intl.supportedValuesOf?.('timeZone');
    if (Array.isArray(zones) && zones.length) return zones;
  } catch (error) {
    // Старый движок — падаем на короткий список ниже.
  }
  return FALLBACK_TIMEZONES;
};

/* ------------------------------------------------------------------ Part 3 */

/** Единственный failure_code, который ТЗ разрешает объяснить пользователю словами. */
export const EVIDENCE_FAILURE = {
  RETENTION_EXPIRED: 'retention_expired',
};

/**
 * PART 3, раздел 3: состояния Evidence в человеческом виде.
 *
 * Технический failure_code пользователю не показывается — из всех причин
 * недоступности имя имеет только истёкшее хранение. Неизвестный статус
 * деградирует до «готовится»: новый статус бэкенда не должен рисовать ошибку
 * там, где фото просто ещё не обработано.
 */
export const describeEvidenceState = ({ status, failureCode } = {}) => {
  if (status === EVIDENCE_STATUS.READY) return { kind: 'ready', text: '' };

  if (status === EVIDENCE_STATUS.EMPTY) return { kind: 'empty', text: 'Фото отсутствует' };

  if (status === EVIDENCE_STATUS.UNAVAILABLE) {
    return failureCode === EVIDENCE_FAILURE.RETENTION_EXPIRED
      ? { kind: 'unavailable', text: 'Фото удалено по сроку хранения' }
      : { kind: 'unavailable', text: 'Фото недоступно' };
  }

  return { kind: 'processing', text: 'Фото готовится' };
};

/* ------------------------------------------------------------------ Part 4 */

/**
 * Состояние правила V2 (Part 4, раздел 3.4). Булева `enabled` недостаточно:
 * удалённое правило тоже приходит с `enabled === false`, но это другое
 * состояние — его нельзя активировать, и в списке оно tombstone.
 */
export const RULE_STATE = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DELETED: 'deleted',
};

export const deriveRuleState = (rule) => {
  if (rule?.deleted_at) return RULE_STATE.DELETED;
  if (rule?.enabled === false) return RULE_STATE.INACTIVE;
  return RULE_STATE.ACTIVE;
};

const RULE_STATE_BADGES = {
  [RULE_STATE.ACTIVE]: { label: 'АКТИВНО', modifier: 'enabled' },
  [RULE_STATE.INACTIVE]: { label: 'НЕАКТИВНО', modifier: 'disabled' },
  [RULE_STATE.DELETED]: { label: 'УДАЛЕНО', modifier: 'deleted' },
};

export const getRuleStateBadge = (state) =>
  RULE_STATE_BADGES[state] ?? RULE_STATE_BADGES[RULE_STATE.INACTIVE];

/**
 * Part 4, раздел 6.4. Минимум два кода; всё остальное, включая пустую причину,
 * деградирует до нейтрального «Правило неактивно» — технический код
 * пользователю не показывается.
 */
const INACTIVE_REASON_LABELS = {
  detector_disabled_by_admin: 'Детекция выключена администратором',
  zone_deleted_by_admin: 'Зона удалена администратором',
};

export const getInactiveReasonLabel = (code) =>
  INACTIVE_REASON_LABELS[code] ?? 'Правило неактивно';

/**
 * origin (ТЗ задача 34). В production openapi поле — строка; старый target
 * contract отдавал { source }. Принимаем обе формы, неизвестное — пустая строка.
 */
const RULE_ORIGIN_LABELS = {
  admin: 'Создано администратором',
  client: 'Предложено пользователем',
  approved_client_request: 'Предложено пользователем, одобрено администратором',
  system: 'Системное',
};

export const getRuleOriginLabel = (origin) => {
  const source = typeof origin === 'string' ? origin : origin?.source;
  return RULE_ORIGIN_LABELS[source] ?? '';
};

export const GENERAL_ZONE_LABEL = 'Общая область контроля';

/**
 * ТЗ задача 7: zone_id=null — общий scope правила, а не «весь кадр».
 * Настоящую зону ищем по stable zone_id в переданном списке; если списка
 * нет — показываем display_name из правила, иначе сам zone_id.
 */
export const getZoneLabel = ({ zoneId, zones = [], fallbackName = '' } = {}) => {
  if (zoneId == null || zoneId === '') return GENERAL_ZONE_LABEL;
  const zone = (Array.isArray(zones) ? zones : []).find((item) => item?.zone_id === zoneId);
  return zone?.display_name || fallbackName || zoneId;
};
